import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { type ProductVariants } from '@/common/types/product-details.type';
import { type VendorItem } from '@/common/types/vendor.type';
import { selectIncreaseCart, useCartStore } from '@/store/use-cart.store';
import { useDomainStore } from '@/store/use-domain.store';
import { useCheckBasket } from '@/common/apis/cart/check-basket';
import Toast from '@/common/components/toast/toast.component';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';
import { resolveStorefrontPath } from '@/common/utils/storefront-path.utility';

interface UseProductCartProps {
  productId: number;
  productVariant: ProductVariants | null;
  productVariants: ProductVariants[] | undefined;
  vendorSlug: string;
}

export const useProductCart = ({
  productId,
  productVariant,
  productVariants,
  vendorSlug,
}: UseProductCartProps) => {
  const router = useRouter();
  const isTenant = useDomainStore((store) => store.isTenant);
  const increaseCart = useCartStore(selectIncreaseCart);

  const {
    mutate: checkBasketMutate,
    isSuccess: basketSuccess,
    isPending,
  } = useCheckBasket({
    productVariantId:
      productVariants?.length === 1
        ? productVariants[0].id ?? 0
        : productVariant?.id ?? 0,
  });

  const routeToCart = useCallback(() => {
    router.push(
      `/cart${createQueryStringUtility({
        from: resolveStorefrontPath(!isTenant ? vendorSlug : null),
      })}`
    );
  }, [router, isTenant, vendorSlug]);

  const addToCart = useCallback(() => {
    if ((productVariants?.length ?? 0) === 1 && productVariant !== null) {
      checkBasketMutate();
    } else if ((productVariants?.length ?? 0) > 1 && productVariant) {
      checkBasketMutate();
    } else {
      Toast.error('لطفا ویژگی دیگر محصول مورد نظر را انتخاب کنید');
    }
  }, [productVariants, productVariant, checkBasketMutate]);

  const increaseProduct = useCallback(
    (vendor: VendorItem) => {
      increaseCart({
        product: {
          productId,
          productVariantId:
            productVariant?.id !== -1
              ? productVariant?.id || 0
              : productVariants?.[0]?.id || 0,
        },
        vendor,
      });
      Toast.callToAction(
        'محصول افزوده شد.',
        null,
        routeToCart,
        'مشاهده سبد خرید',
        'product-click-toast-add-to-cart-v1'
      );
    },
    [productId, productVariant, productVariants, increaseCart, routeToCart]
  );

  const handleBasketSuccess = useCallback(
    (vendor: VendorItem | undefined) => {
      if (!vendor || !productVariant || !basketSuccess) return;

      // Get the latest cart state when the function is called
      const currentCartItems = useCartStore.getState().items;
      const activeCart = currentCartItems.find(
        (cart) => cart.products.length > 0
      );
      const items =
        activeCart?.products?.map((item) => item.productVariantId) ?? [];

      if (
        activeCart?.vendor?.id != null &&
        activeCart.vendor.id !== vendor.id
      ) {
        Toast.error(
          'هر فروشگاه، سبد خرید جداگانه داره! برای اضافه کردن محصول از فروشگاه جدید، اول سبد خرید فعلی‌تون رو نهایی کنین.'
        );
        return;
      }

      const existingProduct = activeCart?.products.find(
        (item) => productVariant?.id === item.productVariantId
      );

      if (existingProduct) {
        if (existingProduct.quantity >= 5) {
          Toast.error('حداکثر ۵ عدد از این محصول قابل خرید است.');
        } else {
          increaseProduct(vendor);
        }
      } else if (!items.includes(productVariant.id ?? 0)) {
        increaseProduct(vendor);
      }
    },
    [productVariant, basketSuccess, increaseProduct]
  );

  return {
    addToCart,
    handleBasketSuccess,
    isPending,
    basketSuccess,
  };
};
